import { DeviceStatusInfo } from "@fairfleet/geotab";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import AltRouteIcon from "@mui/icons-material/AltRoute";
import NavigationIcon from "@mui/icons-material/Navigation";
import PlaceIcon from "@mui/icons-material/Place";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import SignalCellularAltIcon from "@mui/icons-material/SignalCellularAlt";
import SpeedIcon from "@mui/icons-material/Speed";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { useCallback, useMemo, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import FloatingPanel from "./FloatingPanel";
import { useAddresses } from "@/hooks/addresses/useAddresses";
import { useDeviceById } from "@/hooks/devices/useDeviceById";
import { useStatusInfoById } from "@/hooks/statusInfos/useStatusInfoById";
import {
  HandoverPage,
  dispatchInStructureNavigation,
  isAddInPageAvailable,
  isInHomeStructure,
  setPendingDeviceId,
} from "@/utilities/navigation/inStructureNavigation";

interface Props {
  deviceId: string;
  /** Bulk-fetched status info map for immediate display while per-device query loads */
  statusInfoMap?: Record<string, DeviceStatusInfo>;
  /** Last successful fetch timestamp of the bulk status query */
  statusInfoMapUpdatedAt?: number;
  /** Driving state from the 2s position poll — single source of truth matching the map icon */
  isDrivingMap?: Record<string, boolean>;
  onClose: () => void;
}

/** The pages this panel can jump to, as they are registered in `src/public/config.json`. */
const JUMP_TARGETS: Record<HandoverPage, string> = {
  motorData: "motor-data.html",
  route: "route.html",
};

/** Offered in this order, as symbols: two named buttons would push the panel wider. */
const JUMPS: { page: HandoverPage; Icon: typeof ShowChartIcon; labelKey: string }[] = [
  { page: "motorData", Icon: ShowChartIcon, labelKey: "map.openMotorData" },
  { page: "route", Icon: AltRouteIcon, labelKey: "map.openRoute" },
];

export default function VehicleInfoPanel({
  deviceId,
  statusInfoMap,
  statusInfoMapUpdatedAt,
  isDrivingMap,
  onClose,
}: Props) {
  const { t } = useTranslation();
  const { data: statusInfo, dataUpdatedAt: statusInfoUpdatedAt } = useStatusInfoById(
    deviceId,
    [],
    2000,
  );
  const { data: device, isLoading: deviceLoading } = useDeviceById(deviceId);
  const info = statusInfo ?? statusInfoMap?.[deviceId];
  const name = device?.name;
  const speed = info?.speed ?? 0;
  const bearing = info?.bearing ?? 0;
  // Use isDriving from position poll (2s) as single source of truth — matches the map icon
  const isDriving = isDrivingMap?.[deviceId] ?? info?.isDriving ?? false;
  const hasCommStatus = info?.isDeviceCommunicating !== undefined;
  const isCommunicating = info?.isDeviceCommunicating ?? false;

  const coords = useMemo(() => {
    if (info?.latitude == null || info?.longitude == null) return [];
    return [{ x: info.longitude, y: info.latitude }];
  }, [info?.latitude, info?.longitude]);

  const { data: addresses = [] } = useAddresses(coords, false, true);
  const address = addresses[0]?.formattedAddress;

  const durationLabel = info?.currentStateDuration ?? "—";
  const coordLabel =
    info?.latitude != null && info?.longitude != null
      ? `${info.latitude.toFixed(5)}, ${info.longitude.toFixed(5)}`
      : "—";

  /**
   * Opens another page on this vehicle.
   *
   * Inside the launcher the page is swapped in place, which is where the reader almost always is.
   * A page opened on its own has no launcher to ask, so the vehicle goes along in the address and
   * the target page reads it from there.
   */
  const jumpTo = useCallback(
    (page: HandoverPage) => {
      setPendingDeviceId(page, deviceId);
      const addInUrl = JUMP_TARGETS[page];

      if (isInHomeStructure()) {
        dispatchInStructureNavigation(addInUrl);
        return;
      }

      window.open(`${addInUrl}?deviceId=${encodeURIComponent(deviceId)}`, "_blank");
    },
    [deviceId],
  );

  const titleContent = deviceLoading && !name ? <Skeleton variant="text" width={120} /> : name;
  const requestTimestamp =
    statusInfo != null
      ? statusInfoUpdatedAt
      : statusInfoMapUpdatedAt && statusInfoMapUpdatedAt > 0
        ? statusInfoMapUpdatedAt
        : null;
  const requestAgeSeconds =
    requestTimestamp != null
      ? Math.max(0, Math.floor((Date.now() - requestTimestamp) / 1000))
      : null;
  const requestAgeLabel = requestAgeSeconds == null ? "—" : `${requestAgeSeconds}s`;

  return (
    <FloatingPanel
      title={titleContent}
      placement="bottomRight"
      minWidth={260}
      maxWidth={340}
      onClose={onClose}
    >
      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 1.5 }}>
        <Chip
          size="small"
          label={isDriving ? t("map.driving") : t("map.stopped")}
          color={isDriving ? "success" : "default"}
        />
        {hasCommStatus && (
          <Chip
            size="small"
            label={isCommunicating ? t("map.communicating") : t("map.noSignal")}
            color={isCommunicating ? "primary" : "warning"}
          />
        )}
        <Chip
          size="small"
          variant="outlined"
          label={`${t("map.requestAge")}: ${requestAgeLabel}`}
        />
      </Box>

      <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <SpeedIcon fontSize="small" color="action" />
          <Typography variant="body2">
            {Math.round(speed)}
            {" km/h"}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <NavigationIcon
            fontSize="small"
            color="action"
            sx={{ transform: `rotate(${bearing}deg)` }}
          />
          <Typography variant="body2">{Math.round(bearing)}°</Typography>
        </Box>
      </Box>

      <Box sx={{ mt: 1.5, display: "grid", gap: 1 }}>
        <InfoRow
          icon={<AccessTimeIcon fontSize="small" color="action" />}
          label={t("map.stateDuration")}
        >
          {durationLabel}
        </InfoRow>
        <InfoRow icon={<PlaceIcon fontSize="small" color="action" />} label={t("map.coordinates")}>
          {coordLabel}
        </InfoRow>
        <InfoRow icon={<PlaceIcon fontSize="small" color="action" />} label={t("map.address")}>
          {address || "—"}
        </InfoRow>
        {hasCommStatus && (
          <InfoRow
            icon={<SignalCellularAltIcon fontSize="small" color="action" />}
            label={t("map.connectivity")}
          >
            {isCommunicating ? t("map.online") : t("map.offline")}
          </InfoRow>
        )}
      </Box>

      <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 1.5 }}>
        {JUMPS.map(({ page, Icon, labelKey }) =>
          // Pages arrive one at a time, so a jump can name one this build does not have yet.
          // Offered anyway, it would be a button that does nothing.
          isAddInPageAvailable(JUMP_TARGETS[page]) ? (
            <Tooltip key={page} title={t(labelKey)}>
              <IconButton
                size="small"
                color="primary"
                onClick={() => jumpTo(page)}
                aria-label={t(labelKey)}
              >
                <Icon fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : null,
        )}
      </Stack>
    </FloatingPanel>
  );
}

function InfoRow({
  icon,
  label,
  children,
}: {
  icon: ReactNode;
  label: string;
  children: ReactNode;
}) {
  return (
    <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
      {icon}
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="caption" color="text.secondary">
          {label}
        </Typography>
        <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
          {children}
        </Typography>
      </Box>
    </Box>
  );
}
